// Sprint Forecast: React UI on the SidebarWorkspaceLayout template. The maths lives in
// ./model.js. It takes remaining work from the PERT calculator and hands next-sprint
// capacity to Sprint Mix (src/lib/handBack.js).

import { useMemo, useState } from "react";
import { SidebarWorkspaceLayout } from "@/components/templates";
import {
  BarFooter,
  BriefLines,
  Callout,
  ExampleDataPicker,
  Explainer,
  Field,
  Graph,
  MetricCard,
  MetricGrid,
  OpenInTool,
  PillToggle,
  RangeInput,
  UrlStateNotices,
  useUrlRestore,
  useUrlSync,
} from "@/components/layout";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { headingFor, ledeOf } from "@/lib/pageHeadings";
import { hubLink, hubState, initialBack, linkWithBack } from "@/lib/handBack.js";
import { SPREADS, forecast, sprintsText, summaryLines } from "./model.js";
import { EXAMPLES, exampleById } from "./examples.js";
import { SPRINT_UNITS, sprintsShare } from "./share.js";
import { DEFAULT_STATE as MIX_DEFAULT, sprintMixShare } from "@/sprintmix/share.js";

const HEADING = headingFor("tool/sprint-forecast");

/* eslint-disable @typescript-eslint/no-explicit-any */
type Any = any;

interface State {
  history: string;
  remaining: { low: number; high: number };
  unit: string;
  spread: string;
  sprintWeeks: number;
}

const INITIAL = EXAMPLES[0].state as State;
const pct = (p: number) => `${Math.round(p * 100)}%`;

export function App() {
  const shared = useUrlRestore<State>(sprintsShare, (s) => setState(s));
  const [state, setState] = useState<State>(shared.initial ?? INITIAL);
  const [back] = useState(() => initialBack(["sprintmix"]));
  useUrlSync(sprintsShare, () => state, [state], shared);
  const set = (patch: Partial<State>) => setState((s) => ({ ...s, ...patch }));

  const r: Any = useMemo(() => forecast(state), [state]);
  const ok = !r.problems.length;
  const u = state.unit;
  const w = state.sprintWeeks;

  const mixHref = (() => {
    if (!ok || u === "items") return undefined;
    const hub = (hubState(back, sprintMixShare) as Any) ?? MIX_DEFAULT;
    const round = (x: number) => Math.round(x * 10) / 10;
    return hubLink("/tool/sprint-planner", sprintMixShare, {
      ...hub,
      capacityUnit: u,
      capacity: { ...hub.capacity, build: { low: round(r.next.low), high: round(r.next.high) } },
    });
  })();

  return (
    <SidebarWorkspaceLayout
      label="Unit 36 / Sprint forecast"
      tags={<span className="hidden md:inline">Local · nothing leaves the page</span>}
      eyebrow="Sprint forecast"
      title={HEADING.h1}
      lede={ledeOf(HEADING)}
      sidebar={
        <div className="grid gap-5 p-5">
          <h2 className="sr-only">Inputs</h2>
          <UrlStateNotices state={shared} />
          <ExampleDataPicker
            examples={EXAMPLES.map(({ id, name, description }) => ({ id, name, description }))}
            onLoad={(id) => {
              const ex = exampleById(id);
              if (ex) setState(ex.state as State);
            }}
            deepLink={!shared.initial}
          />
          <p className="m-0 text-xs text-muted-foreground">Examples are illustrative: invented velocities.</p>
          <Field label="01 / Past sprints" hint="Work done per sprint, oldest first">
            <Textarea
              aria-label="Past sprint velocities"
              className="min-h-20 font-mono text-sm"
              value={state.history}
              onChange={(e) => set({ history: e.target.value })}
              spellCheck={false}
            />
          </Field>
          <Field label="Unit">
            <Select aria-label="Unit" value={u} onChange={(e) => set({ unit: e.target.value })}>
              {SPRINT_UNITS.map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="02 / Remaining work" hint="A 90% range; equal ends if you know it">
            <RangeInput label="Remaining work" value={state.remaining} min={0} onChange={(remaining) => set({ remaining })} unit={u} />
          </Field>
          <a className="text-sm" href={linkWithBack("/tool/pert-calculator", sprintsShare, state)}>
            Estimate it task by task in the PERT calculator →
          </a>
          <Field label="Sprint length (weeks)">
            <Input
              type="number"
              min={0.5}
              max={8}
              step={0.5}
              aria-label="Sprint length in weeks"
              className="h-8 w-24 rounded-none font-mono text-xs"
              value={String(w)}
              onChange={(e) => set({ sprintWeeks: Math.min(8, Math.max(0.5, Number(e.target.value) || 2)) })}
            />
          </Field>
          <Field label="03 / How much velocity moves" hint="Prior, worth two sprints">
            <PillToggle aria-label="How much velocity moves" value={state.spread} onChange={(v) => set({ spread: v })} options={SPREADS.map((s) => ({ value: s.id, label: s.label }))} />
          </Field>
        </div>
      }
      footer={
        <BarFooter>
          <span>Lognormal velocity · Bayesian spread · simulated sprints</span>
          <span>{ok ? `85% done in ${sprintsText(r.s85, w)}` : "Enter history and remaining work"}</span>
        </BarFooter>
      }
    >
      <div className="p-5">
        <Explainer id="sprints" title="What this answers" summary="When the work is done, as a chance" defaultOpen>
          <p>
            A date from average velocity is right about half the time. This page treats velocity as uncertain twice
            over: sprints differ from each other, and a few sprints say only so much about the team&rsquo;s true pace.
            It simulates thousands of futures and counts how often the work is done by each sprint.
          </p>
          <p>With three sprints the range is wide. Each new sprint narrows it.</p>
        </Explainer>
        {r.excluded.length > 0 && (
          <Callout kind="warn" className="mb-4">
            <strong>Not counted:</strong> {r.excluded.map((e: Any) => `"${e.token}" (${e.reason})`).join("; ")}.
          </Callout>
        )}
        {!ok ? (
          <Callout kind="error">{r.problems.join(" ")}</Callout>
        ) : (
          <>
            <MetricGrid className="lg:grid-cols-3">
              <MetricCard eyebrow="Even odds" value={sprintsText(r.s50, w)}>
                Half the futures finish later than this.
              </MetricCard>
              <MetricCard accent eyebrow="85% chance done in" value={sprintsText(r.s85, w)} data-value={r.s85}>
                A date you keep about 6 times in 7.
              </MetricCard>
              <MetricCard eyebrow="95% chance done in" value={sprintsText(r.s95, w)}>
                Only 1 future in 20 takes longer.
              </MetricCard>
              <MetricCard eyebrow="Average-velocity plan" value={sprintsText(r.averagePlan, w)}>
                Done by then {pct(r.chanceByAveragePlan)} of the time.
              </MetricCard>
              <MetricCard eyebrow="Next sprint" value={`${Math.round(r.next.median)} ${u}`}>
                90% range {Math.round(r.next.low)} to {Math.round(r.next.high)} {u}.
              </MetricCard>
              <MetricCard eyebrow="History used" value={`${r.values.length} sprint${r.values.length === 1 ? "" : "s"}`}>
                Average {Math.round(r.average * 10) / 10} {u} a sprint.
              </MetricCard>
            </MetricGrid>
            <Graph
              className="mt-6"
              title="Chance the work is done by each sprint"
              caption="Each point is the share of simulated futures done by the end of that sprint. Conditional on future sprints behaving like the past ones."
              series={[
                {
                  id: "done",
                  label: "Chance done by",
                  points: r.doneBy.slice(0, r.horizon).map((p: number, i: number) => [i + 1, p]),
                  kind: "area",
                  tone: "accent",
                  width: 2.5,
                },
              ]}
              markers={[
                { x: r.averagePlan, label: "Average-velocity plan", tone: "ink", dashed: true },
                ...(Number.isFinite(r.s85) ? [{ x: r.s85, label: "85%", tone: "muted" as const }] : []),
              ]}
              yDomain={[0, 1]}
              showY
              formatY={(v) => `${Math.round(v * 100)}%`}
              formatX={(v) => `${Math.round(v)}`}
              xLabel="Sprint"
            />
            <Graph
              className="mt-6"
              title="Work done by each sprint"
              caption={`Cumulative ${u} done: the middle line is the median future, bands hold 50% and 90% of futures. The work is done once the band passes ${Math.round(Math.sqrt(state.remaining.low * state.remaining.high))} ${u}, the middle of the remaining range.`}
              series={[{ id: "median", label: "Median", points: r.fan.map((f: Any) => [f.sprint, f.q50]), tone: "ink", width: 2 }]}
              ribbons={[
                { id: "90", label: "90% of futures", lower: r.fan.map((f: Any) => [f.sprint, f.q5]), upper: r.fan.map((f: Any) => [f.sprint, f.q95]), tone: "accent" },
                { id: "50", label: "50% of futures", lower: r.fan.map((f: Any) => [f.sprint, f.q25]), upper: r.fan.map((f: Any) => [f.sprint, f.q75]), tone: "accent" },
              ]}
              showY
              formatX={(v) => `${Math.round(v)}`}
              xLabel="Sprint"
              yLabel={u}
            />
            <Callout kind="note" className="mt-6">
              <strong>Resampling past sprints</strong> (the common Monte Carlo forecast) gives {sprintsText(r.resampled.s50, w)} at
              even odds and {sprintsText(r.resampled.s85, w)} at 85%. It cannot produce a sprint faster or slower than one already
              seen, so with short history it is usually too narrow.
            </Callout>
            <BriefLines className="mt-6" lines={summaryLines(r, u)} />
            <OpenInTool
              title="Use this forecast"
              targets={[
                {
                  id: "sprintmix",
                  name: "Sprint Mix",
                  use: back
                    ? "Return to your sprint plan with the next sprint's 90% range as the build team's capacity."
                    : "Plan the next sprint with its 90% velocity range as the build team's capacity.",
                  href: mixHref,
                  reason: "Sprint Mix sizes work in points or days. Switch the unit to one of those.",
                },
              ]}
              note="Only the capacity range and its unit travel."
            />
          </>
        )}
      </div>
    </SidebarWorkspaceLayout>
  );
}
